import React, { useState } from "react";
import { ArrowLeft, ChevronDown, ChevronUp, Mail, Phone, HelpCircle } from "lucide-react";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";

const HEADER_LOGO_URL = "/header_logo.jpeg";

export default function FAQPage() {
  const [expandedIndex, setExpandedIndex] = useState(null);

  const toggleFAQ = (index) => {
    setExpandedIndex(expandedIndex === index ? null : index);
  };

  const faqs = [
    {
      category: "General",
      questions: [
        {
          q: "What is Kavach FireSight Pro?",
          a: "Kavach FireSight Pro is an AI-powered fire safety compliance platform that reads architectural and MEP drawings (PDF, DXF, DWG, ZIP tender bundles) and automatically produces a complete Bill of Quantities (BOQ), checks compliance against NBC 2016 / NFPA 13-14-20-72 / IS standards and 27 region-specific codes, and surfaces the gap between what's drawn, what's required, what the architect has costed, and what the Fire Department NOC has approved."
        },
        {
          q: "Who is it for?",
          a: "Fire-safety consultants, MEP contractors, architects, MSME bidders preparing tenders, government auditors, and building owners commissioning new properties. The tool replaces 3–5 days of manual symbol counting and BOQ assembly with a 2–5 minute analysis."
        },
        {
          q: "Which standards and regions are supported?",
          a: "NBC 2016 (all parts), NBC 2026 amendments, NFPA 13/14/20/72, IS 15105 / 2189 / 15657, plus 15 Indian state-specific rules (Maharashtra, Delhi, Karnataka, Gujarat, Tamil Nadu, Telangana, Rajasthan, Uttar Pradesh, West Bengal, Kerala, Andhra Pradesh, Madhya Pradesh, Punjab, Haryana, Goa), and international codes for UAE, Qatar, Saudi Arabia, Oman, Bahrain, Kuwait, Egypt, South Africa, Nigeria, Kenya, USA (NFPA reference), UK (BS 9999), Singapore SCDF, Australia AS/NZS, Brazil — 27 regions in total."
        },
        {
          q: "What's new in 2026?",
          a: "Architect-BOQ comparator (one-click compare against the architect's xlsx), Fire NOC vs BOQ third axis, ZIP/tender-bundle upload, DWG auto-conversion via CloudConvert (premium), multi-page PDF auto-splitter by floor, smart project-scope interpreter that scales a single floor BOQ into whole-building quantities, and a transparent Reasoning tab that shows exactly how the tool got from one floor to the full project."
        }
      ]
    },
    {
      category: "Project Scope & Building-wide Scaling",
      questions: [
        {
          q: "I only have ONE floor plan. Can the tool still give me a building-wide BOQ?",
          a: "Yes — just type your scope in the Project Scope box (e.g. \"7 buildings, 7 floors each, residential, basement-2 parking, terrace tank, this is the typical floor\"). The tool parses your description, applies NBC profiles per floor type (basement/ground/typical/podium/terrace each behave differently), keeps building-wide systems (UG tank, pumps, FACP) at 1×, and multiplies the rest. Open the Reasoning tab to see the exact math line-by-line."
        },
        {
          q: "How does the tool know which floor I uploaded?",
          a: "Two ways. First, if your title block reads \"Typical Floor\" / \"Ground Floor\" / \"Basement-2\" the tool picks it up automatically. Second, you can declare it in the scope text — e.g. \"this is the ground floor of one tower\". You can override it from the Reasoning tab if the assumption is wrong."
        },
        {
          q: "Can I just write free-form English in the scope box?",
          a: "Yes. \"G+5 office, ground for retail, ordinary hazard\" / \"two towers fourteen floors each hospital plus OPD\" / \"warehouse 8000 sqm extra hazard with mezzanine\" — the AI extracts num_buildings, floors_per_building, basements, occupancy and hazard automatically."
        }
      ]
    },
    {
      category: "File Upload & Analysis",
      questions: [
        {
          q: "What file formats are supported?",
          a: "PDF (vector or raster), PNG, JPG, DXF, DWG and ZIP/RAR tender bundles. DWG conversion uses CloudConvert (premium tier). Max sizes: Lite 10 MB, Pro 50 MB, Pro Plus 200 MB. Recommended drawing resolution for raster scans is 2400×1600 px or higher."
        },
        {
          q: "What's inside a ZIP tender bundle?",
          a: "Drop the architect's full archive — drawings (PDF/DXF/DWG), the BOQ.xlsx, the Fire NOC PDF, tech specs. The tool inventories everything, parses the BOQ, OCRs the NOC, and lets you pick which drawing to analyse first."
        },
        {
          q: "How long does analysis take?",
          a: "Single floor: 60–120 s. Multi-floor (10 floors): 5–10 min. ZIP bundle: depends on file count — drawings are analysed on demand. DWG → PDF conversion adds 30–60 s per file."
        },
        {
          q: "Can I analyse multi-page PDFs as separate floors?",
          a: "Yes. Use the \"Split by floor\" endpoint or just upload the multi-page PDF in Pro / Pro Plus — every page becomes its own floor analysis and the cumulative building BOQ is auto-generated. Floor names are auto-detected from each page's title block."
        },
        {
          q: "How accurate is the detection?",
          a: "Vector PDFs / DXF (block-aware): typically 95-98% on equipment counts. Raster scans: 75-90% depending on resolution and legend clarity. We surface a confidence score per BOQ row and a \"needs review\" badge when CV and AI disagree. For sign-off on critical projects, a qualified consultant should always validate."
        }
      ]
    },
    {
      category: "Tiers & Pricing",
      questions: [
        {
          q: "Which plans are available?",
          a: "Lite (₹2,000/month — 5 analyses, single floor, online viewing only), Pro (₹8,000/month — multi-floor up to 10, fittings detection, pipe lengths, Excel/PDF exports, DWG auto-convert, ZIP upload, Architect-BOQ + NOC comparator), Pro Plus (Contact Sales — unlimited floors, legend-driven detection, pipe-by-diameter, white-label reports, API access, dedicated support)."
        },
        {
          q: "What if I exceed the 5 analyses cap?",
          a: "Pay-per-analysis pricing kicks in on Lite and Pro, or upgrade your plan. The monthly counter resets on the 1st of every month in IST."
        },
        {
          q: "How do I get a free trial?",
          a: "Email info@kavachinfra.in with your company name and we'll provision a 7-day Pro trial. MSMEs registered under the Udyam portal get a 60-day extended trial."
        }
      ]
    },
    {
      category: "Compliance Outputs",
      questions: [
        {
          q: "What's in the BOQ?",
          a: "Sprinklers (by type + temperature rating), fire hydrant posts and landing valves, smoke / heat detectors, FACPs, MCPs, fire extinguishers, hose reels, fire cabinets, pipes (total length, plus by-diameter on Pro Plus), fittings (elbows / tees / reducers / flanges / couplings / unions on Pro+), supports, hangers, gaskets, fasteners. Required-by-NBC vs Detected-on-drawing are shown side-by-side."
        },
        {
          q: "How does the Architect-BOQ vs Tool gap work?",
          a: "Upload the architect's xlsx; the tool parses every line, normalises the labels, and compares against (a) NBC required, (b) drawing detected, (c) Super-High-Rise checks for buildings >70 m. Each row is flagged high / medium / OK so the consultant can focus on what matters."
        },
        {
          q: "What's the NOC third axis?",
          a: "Upload the project's Fire NOC PDF; Gemini Vision extracts the approved baseline (tank capacity, pump duty, sprinkler / detection / signage mandates). The tool then surfaces every gap between what the Fire Department has approved and what the architect's BOQ actually has — a frequent omission source."
        },
        {
          q: "Can I download reports?",
          a: "Pro and Pro Plus include Excel, CSV, PDF and Full-PDF exports. Pro Plus adds white-label branding."
        }
      ]
    },
    {
      category: "Account & Access",
      questions: [
        {
          q: "How do I get started?",
          a: "Visit kavachinfra.in, click the Kavach FireSight Pro login, sign in with Google, and you're in. New accounts default to Lite. Email info@kavachinfra.in to upgrade."
        },
        {
          q: "Why is my account pending approval?",
          a: "New accounts go through a quick approval to prevent abuse — typically <24 h. Admins are auto-approved."
        }
      ]
    },
    {
      category: "Technical",
      questions: [
        {
          q: "What if my upload fails?",
          a: "Check file size vs your tier limit, ensure format is supported (PDF/DXF/DWG/ZIP/PNG/JPG), and confirm internet connection is stable. ZIPs over 500 MB should be split."
        },
        {
          q: "Why does the BOQ have items not on my drawing?",
          a: "Items shown in BLUE are NBC-REQUIRED but missing or under-counted on the drawing. Items in GREEN are DETECTED on the drawing. The gap between the two is the consultant's review queue."
        },
        {
          q: "Can I use this on mobile?",
          a: "The Dashboard works on tablets and smartphones, but the Analysis page (Equipment / BOQ / Reasoning / etc.) is optimised for desktop. We recommend a 13\"+ screen for review."
        }
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 shadow-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <img src={HEADER_LOGO_URL} alt="Kavach Logo" className="h-14 w-14 object-contain rounded-full" />
              <div>
                <h1 className="text-xl font-bold" style={{color: '#003366'}}>KAVACH INFRA SOLUTIONS LLP</h1>
                <p className="text-xs" style={{color: '#D4AF37', fontWeight: 600}}>रक्षा | सुरक्षा | सजगता</p>
              </div>
            </div>
            <Button
              variant="outline"
              onClick={() => window.location.href = '/'}
              className="flex items-center gap-2"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Home
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-5xl mx-auto px-6 py-12">
        {/* Hero Section */}
        <div className="text-center mb-12">
          <div className="flex items-center justify-center gap-3 mb-4">
            <HelpCircle className="w-12 h-12" style={{color: '#003366'}} />
            <h1 className="text-4xl font-bold" style={{color: '#003366'}}>
              Frequently Asked Questions
            </h1>
          </div>
          <p className="text-lg text-slate-600 max-w-3xl mx-auto">
            Find answers to common questions about Kavach FireSight Pro. Can't find what you're looking for? 
            Contact our support team below.
          </p>
        </div>

        {/* FAQ Sections */}
        <div className="space-y-8">
          {faqs.map((category, categoryIndex) => (
            <div key={categoryIndex}>
              <h2 className="text-2xl font-bold mb-4" style={{color: '#003366'}}>
                {category.category}
              </h2>
              <div className="space-y-3">
                {category.questions.map((faq, faqIndex) => {
                  const globalIndex = `${categoryIndex}-${faqIndex}`;
                  const isExpanded = expandedIndex === globalIndex;
                  
                  return (
                    <Card 
                      key={faqIndex}
                      className={`cursor-pointer transition-all hover:shadow-md ${
                        isExpanded ? 'ring-2 ring-blue-200' : ''
                      }`}
                      onClick={() => toggleFAQ(globalIndex)}
                    >
                      <CardContent className="p-5">
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex-1">
                            <h3 className="text-lg font-semibold mb-2" style={{color: '#003366'}}>
                              {faq.q}
                            </h3>
                            {isExpanded && (
                              <p className="text-slate-700 leading-relaxed mt-3">
                                {faq.a}
                              </p>
                            )}
                          </div>
                          <div>
                            {isExpanded ? (
                              <ChevronUp className="w-5 h-5 text-blue-600" />
                            ) : (
                              <ChevronDown className="w-5 h-5 text-slate-400" />
                            )}
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            </div>
          ))}
        </div>

        {/* Contact Section */}
        <Card className="mt-12 border-2" style={{borderColor: '#D4AF37'}}>
          <CardContent className="p-8">
            <h2 className="text-2xl font-bold mb-4 text-center" style={{color: '#003366'}}>
              Still Have Questions?
            </h2>
            <p className="text-center text-slate-600 mb-6">
              Our team is here to help! Reach out to us for any queries, technical support, or custom requirements.
            </p>
            <div className="grid md:grid-cols-2 gap-6 max-w-2xl mx-auto">
              <div className="flex items-center gap-3 p-4 bg-blue-50 rounded-lg">
                <Mail className="w-6 h-6" style={{color: '#003366'}} />
                <div>
                  <p className="text-sm text-slate-600 font-medium">Email Us</p>
                  <a 
                    href="mailto:info@kavachinfra.in" 
                    className="text-blue-600 font-semibold hover:underline"
                  >
                    info@kavachinfra.in
                  </a>
                </div>
              </div>
              <div className="flex items-center gap-3 p-4 bg-blue-50 rounded-lg">
                <Phone className="w-6 h-6" style={{color: '#003366'}} />
                <div>
                  <p className="text-sm text-slate-600 font-medium">Call Us</p>
                  <a 
                    href="tel:+919876543210" 
                    className="text-blue-600 font-semibold hover:underline"
                  >
                    +91 98765 43210
                  </a>
                </div>
              </div>
            </div>
            <div className="text-center mt-6">
              <p className="text-sm text-slate-500">
                Business Hours: Monday - Saturday, 9:00 AM - 6:00 PM IST<br />
                Response Time: Within 24 hours
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Additional Resources */}
        <div className="mt-8 text-center">
          <p className="text-slate-600 mb-4">
            Looking for more information?
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Button
              variant="outline"
              onClick={() => window.location.href = '/privacy-policy'}
            >
              Privacy Policy
            </Button>
            <Button
              variant="outline"
              onClick={() => window.location.href = '/terms-and-conditions'}
            >
              Terms & Conditions
            </Button>
            <Button
              variant="outline"
              onClick={() => window.location.href = '/career'}
            >
              Careers
            </Button>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="mt-16 bg-slate-900 text-white py-8">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <p className="text-slate-300">
            © 2025 Kavach Infra Solutions LLP. All rights reserved.
          </p>
          <p className="text-slate-400 text-sm mt-2">
            Building Safety Through Innovation | रक्षा | सुरक्षा | सजगता
          </p>
        </div>
      </footer>
    </div>
  );
}
